import { useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import { CustomButton } from "src/design/CustomButton";
import { InfoCard } from "src/design/InfoCard";
import { SnackBar } from "src/design/SnackBar";
import {
  colorTextPrimary,
  colorTextMuted,
  colorActionDanger,
  colorTextError,
} from "src/design/tokens";
import { useDeleteAdminStaff } from "src/data/admin/queries/staff";

const DeleteStaff = () => {
  const [email, setEmail] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "warning" | "error";
  }>({ open: false, message: "", severity: "success" });

  const deleteMutation = useDeleteAdminStaff((result) => {
    setConfirmOpen(false);
    setEmail("");
    setSnack({
      open: true,
      message: result.organizationDeleted
        ? `Staff "${result.email}" owned an organization — the entire organization was deleted with them.`
        : `Staff "${result.email}" deleted.`,
      severity: result.organizationDeleted ? "warning" : "success",
    });
  });

  const trimmedEmail = email.trim();

  const handleDelete = () => {
    if (!trimmedEmail) return;
    deleteMutation.mutate(trimmedEmail, {
      onError: (err) =>
        setSnack({
          open: true,
          message: err instanceof Error ? err.message : "An error occurred",
          severity: "error",
        }),
    });
  };

  return (
    <>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: colorTextPrimary }}
          >
            Delete Staff
          </Typography>
          <Typography variant="body2" sx={{ color: colorTextMuted, mt: 0.5 }}>
            Permanently remove a back-office staff member by email. If the staff
            owns an organization, the whole organization is deleted with them.
          </Typography>
        </Box>

        <InfoCard
          title="Delete by email"
          icon={<PersonRemoveOutlinedIcon fontSize="small" />}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Staff email"
                placeholder="staff@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && trimmedEmail) setConfirmOpen(true);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon
                        sx={{ color: colorTextMuted, fontSize: 18 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CustomButton
                startIcon={<PersonRemoveOutlinedIcon />}
                disabled={!trimmedEmail || deleteMutation.isPending}
                onClick={() => setConfirmOpen(true)}
                sx={{
                  width: "100%",
                  backgroundColor: colorActionDanger,
                  color: "#fff",
                  "&:hover": { backgroundColor: colorTextError },
                }}
              >
                Delete Staff
              </CustomButton>
            </Grid>
          </Grid>
        </InfoCard>
      </Box>

      {/* Delete confirmation */}
      <Dialog
        open={confirmOpen}
        onClose={() => !deleteMutation.isPending && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Staff</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the staff member{" "}
            <strong>{trimmedEmail}</strong>? If they own an organization, every
            other staff member and role in that organization is deleted too.
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton
            color="inverted"
            onClick={() => setConfirmOpen(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </CustomButton>
          <CustomButton
            disabled={deleteMutation.isPending}
            onClick={handleDelete}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{
              backgroundColor: colorActionDanger,
              color: "#fff",
              "&:hover": { backgroundColor: colorTextError },
            }}
          >
            Delete
          </CustomButton>
        </DialogActions>
      </Dialog>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};

export default DeleteStaff;
